import { useContext, useState, Fragment } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Input, Stack, Typography } from '@mui/joy';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import ChatListItem from './ChatListItem';
import AdSlot from '../Ads/user/AdSlot';
import { APIGetChats } from '../../api';
import { LayoutContext } from '../../context';
import PageHeader from '../ui/PageHeader';
import EmptyState from '../ui/EmptyState';
import FilterModal from './FilterModal';
import ModalCommunity from './ModalCommunity';

export default function ChatsPane({ chats = [], setChats, selectedChatId, handleChatSelect,
  onApplyFilters, onSearch, searchQuery, activeFilters = {}, loading, isFiltered }) {
  const { token, currentUser } = useContext(LayoutContext);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [createCommunityModalOpen, setCreateCommunityModalOpen] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [communityError, setCommunityError] = useState('');
  const type = activeFilters.type || 'all';
  const extraFilterCount = Object.entries(activeFilters).filter(([key, value]) => key !== 'type' && (Array.isArray(value) ? value.length : Boolean(value))).length;

  const refreshCommunities = async (communityId) => {
    try {
      const data = await APIGetChats(token);
      const items = data?.chats || [];
      setChats(items);
      if (communityId) {
        const created = items.find(chat => String(chat.chat_id ?? chat.id) === String(communityId));
        if (created) handleChatSelect(created);
      }
      setCommunityError('');
    } catch (_) { setCommunityError('Сообщество сохранено. Обновите страницу, чтобы увидеть изменения.'); }
  };

  const canEdit = chat => currentUser?.id != null && chat.is_community && chat.members?.some(member =>
    member.profile_data?.user_id != null && String(member.profile_data?.user_id) === String(currentUser?.id) && ['creator', 'admin'].includes(member.role));

  return (
    <>
      <Box className="lv-chat-list">
        <PageHeader title="Чаты" contained={false} actions={
          <>
            <IconButton variant="soft" color="primary" aria-label="Фильтры чатов" onClick={() => setFilterModalOpen(true)}
              sx={{ minWidth: 40, minHeight: 40, borderRadius: '14px', position: 'relative' }}>
              <FilterListRoundedIcon sx={{ color: 'inherit' }} />
              {extraFilterCount > 0 && <Box component="span" className="lv-chat-filter-dot" />}
            </IconButton>
            <Button className="lvc-grad-btn" aria-label="Создать сообщество" onClick={() => setCreateCommunityModalOpen(true)}
              sx={{ minWidth: 40, minHeight: 40, px: { xs: 1, sm: 1.5 }, borderRadius: '14px' }}>
              <AddRoundedIcon sx={{ color: 'inherit', fontSize: 21 }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>Создать</Box>
            </Button>
          </>
        }>
          <Typography level="body-sm" sx={{ color: 'var(--lvc-ink-soft)', mb: 1.5 }}>Здесь начинаются ваши разговоры</Typography>
          <Input value={searchQuery} onChange={event => onSearch(event.target.value)}
            startDecorator={<SearchRoundedIcon sx={{ color: 'var(--lvc-ink-soft)' }} />}
            endDecorator={loading ? <CircularProgress size="sm" aria-label="Поиск чатов" /> : searchQuery ? (
              <IconButton size="sm" variant="plain" aria-label="Очистить поиск" onClick={() => onSearch('')}><CloseRoundedIcon sx={{ fontSize: 18, color: 'inherit' }} /></IconButton>
            ) : null}
            placeholder="Поиск по чатам и сообщениям" aria-label="Поиск по чатам и сообщениям"
            sx={{ minHeight: 44, borderRadius: '16px', borderColor: 'var(--lvc-line)', backgroundColor: '#fff', boxShadow: '0 3px 12px rgba(96,40,160,.04)', fontSize: 13.5 }} />
          <Box role="group" aria-label="Тип чатов" className="lv-chat-tabs">
            {[['all', 'Все'], ['personal', 'Личные'], ['community', 'Сообщества']].map(([value, label]) => (
              <button key={value} type="button" aria-pressed={type === value} className={type === value ? 'is-active' : ''}
                onClick={() => onApplyFilters({ ...activeFilters, type: value })}>{label}</button>
            ))}
          </Box>
        </PageHeader>

        <Box className="lv-chat-list__content lvc-scroll">
          {extraFilterCount > 0 && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 2, alignItems: 'center' }}>
              {activeFilters.city && <Chip size="sm" variant="soft">{activeFilters.city}</Chip>}
              {!!activeFilters.tags?.length && <Chip size="sm" variant="soft">Теги: {activeFilters.tags.length}</Chip>}
              {activeFilters.memberCount && <Chip size="sm" variant="soft">По числу участников</Chip>}
              <Button size="sm" variant="plain" onClick={() => onApplyFilters({ type })}>Сбросить фильтры</Button>
            </Stack>
          )}
          {communityError && <Typography level="body-sm" color="warning" sx={{ mb: 2 }}>{communityError}</Typography>}
          {loading && !chats.length ? (
            <Box aria-label="Загрузка списка чатов" sx={{ display: 'grid', gap: 1 }}>{[0, 1, 2].map(index => <Box key={index} className="lvc-skeleton" sx={{ height: 86, borderRadius: '20px' }} />)}</Box>
          ) : !chats.length ? (
            <Box sx={{ maxWidth: 480, mx: 'auto', py: { xs: 2, sm: 4 } }}>
              <EmptyState icon={<ForumRoundedIcon sx={{ color: 'inherit' }} />}
                title={isFiltered ? 'Ничего не найдено' : 'Начните новое знакомство'}
                text={isFiltered ? 'Попробуйте другой запрос или уберите фильтры.' : 'Ваши диалоги появятся здесь. Ответьте на симпатию или создайте сообщество по интересам.'}
                action={isFiltered ? <Button variant="soft" onClick={() => { onSearch(''); onApplyFilters({}); }}>Сбросить поиск</Button> : (
                  <Button className="lvc-grad-btn" startDecorator={<AddRoundedIcon sx={{ color: 'inherit' }} />} onClick={() => setCreateCommunityModalOpen(true)}>Создать сообщество</Button>
                )} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Typography level="body-xs" sx={{ color: 'var(--lvc-ink-soft)', mb: 0.5, px: 0.5 }}>{isFiltered ? 'Результаты поиска' : 'Ваши разговоры'} · {chats.length}</Typography>
              {chats.map((chat, index) => <Fragment key={String(chat.id ?? chat.chat_id ?? index)}>
                {index === 3 && <AdSlot placement="chat_list" />}
                <ChatListItem chat={chat} index={index} onSelect={handleChatSelect}
                  selected={String(selectedChatId) === String(chat.id ?? chat.chat_id)}
                  onEdit={setEditingCommunity} showEditButton={canEdit(chat)} />
              </Fragment>)}
            </Box>
          )}
        </Box>
      </Box>
      <FilterModal open={filterModalOpen} onClose={() => setFilterModalOpen(false)} onApplyFilters={onApplyFilters} initialFilters={activeFilters} />
      <ModalCommunity open={createCommunityModalOpen} onClose={() => setCreateCommunityModalOpen(false)} onCommunityCreated={refreshCommunities} mode="create" />
      <ModalCommunity open={!!editingCommunity} onClose={() => setEditingCommunity(null)}
        onCommunityUpdated={() => { refreshCommunities(); setEditingCommunity(null); }} communityId={editingCommunity?.chat_id ?? editingCommunity?.id} mode="edit" />
    </>
  );
}
